<template>
  <div
    class="login-two flex h-screen max-w-6xl mx-auto w-full flex-col lg:flex-row items-center justify-center gap-10 md:flex-row"
  >
    <div
      class="w-full lg:w-3/6 mx-auto flex flex-col gap-2 bg-white shadow-lg rounded-lg p-4 lg:p-8"
    >
      <NuxtLink
        to="/"
        class="flex flex-row items-center justify-between gap-2 cursor-pointer"
      >
        <div class="w-full flex items-center justify-start">
          <GlobalLogo class="h-12" @click="$router.push('/')" />
        </div>
        <div
          class="w-full flex flex-row items-center justify-between border border-orange-500 rounded gap-2 px-4 py-2"
        >
          <span class="text-orange-500">
            {{ $t("home") }}
          </span>
          <div class="landing-light-orange p-1 rounded">
            <Icon
              name="meteor-icons:arrow-up-right"
              class="size-5 text-orange-500"
            />
          </div>
        </div>
      </NuxtLink>
      <NuxtLink
        to="/register"
        class="flex flex-row items-center justify-between landing-light-orange rounded gap-2 text-nowrap mt-4 p-2"
      >
        <div class="w-full text-center">
          <span class="text-orange-500 font-bold text-sm">
            {{ $t("dont_have_an_account1") }}
          </span>
        </div>
        <div class="w-full bg-orange-500 text-center my-1 py-1 rounded">
          <span class="text-white text-sm font-medium cursor-pointer">
            {{ $t("create_account1") }}
          </span>
        </div>
      </NuxtLink>

      <NuxtLink
        to="https://youtube.com/shorts/S83zZfiaNVM"
        target="_blank"
        class="flex flex-row items-center justify-evenly landing-light-orange rounded gap-2 text-nowrap mt-4 p-2"
      >
        <span class="text-orange-500 font-bold text-sm text-wrap">
          {{ $t("how_to_login_yt") }}
        </span>
        <Icon name="logos:youtube-icon" class="size-10 animate-pulse" />
      </NuxtLink>
      <div class="mt-2">
        <form method="POST" action="" @submit.prevent="onSubmit" novalidate>
          <div class="space-y-4">
            <Field
              v-slot="{ field, errorMessage, handleChange, handleBlur }"
              name="email"
              rules="required|email"
            >
              <BaseInput
                :model-value="field.value"
                :error="errorMessage"
                :disabled="isSubmitting"
                icon="material-symbols:mail"
                type="email"
                :label="$t('email')"
                :placeholder="'ornek@ornekmail.com'"
                @update:model-value="handleChange"
                @blur="handleBlur"
                @keyup.enter="onSubmit"
              />
            </Field>
            <Field
              v-slot="{ field, errorMessage, handleChange, handleBlur }"
              name="password"
              rules="required"
            >
              <BaseInput
                icon="material-symbols:lock"
                :model-value="field.value"
                :error="errorMessage"
                :disabled="isSubmitting"
                type="password"
                :label="$t('password')"
                :placeholder="$t('password')"
                @update:model-value="handleChange"
                @blur="handleBlur"
                @keyup.enter="onSubmit"
              />
            </Field>
          </div>

          <div class="mt-6 flex flex-row gap-2 items-center justify-between">
            <Field
              v-slot="{ field, errorMessage, handleChange, handleBlur }"
              name="trustDevice"
            >
              <BaseCheckbox
                :model-value="field.value"
                :disabled="isSubmitting"
                shape="rounded"
                :label="$t('remember_this_member')"
                color="primary"
                @update:model-value="handleChange"
                @blur="handleBlur"
                @keyup.enter="onSubmit"
              />
            </Field>

            <NuxtLink @click="$router.push('/forgot')">
              <span
                class="text-orange-500 text-sm border-b border-orange-500 leading-0 cursor-pointer"
              >
                {{ $t("forgot_my_password1") }}
              </span>
            </NuxtLink>
          </div>
          <div class="mt-6 pb-4">
            <div
              class="flex flex-col xl:flex-row w-full rounded-md shadow-sm gap-2"
            >
              <BaseButton
                @click="onSubmit"
                :disabled="isSubmitting"
                :loading="isSubmitting"
                shape="rounded"
                class="w-full !bg-orange-500 !text-white mr-2"
              >
                {{ $t("login") }}
              </BaseButton>
            </div>

            <div class="flex items-center justify-center mt-4 p-2">
              <GoogleSignin />
            </div>
          </div>

          
        </form>
      </div>
    </div>
    <AuthLandingImage />
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from "@/stores/user"
import { Field, useForm } from "vee-validate"
import { useI18n } from "vue-i18n"
const { t } = useI18n()
const userStore = useUserStore()

const initialValues = computed(() => ({
  email: "",
  password: "",
  trustDevice: false,
}))

const { handleSubmit, isSubmitting } = useForm({
  initialValues,
})

const router = useRouter()
const toaster = useToaster()

const onSubmit = handleSubmit(async (values) => {
  const { data, error } = await useCustomFetch("/api/login", {
    method: "post",
    body: values,
  })

  if (data.value?.success) {
    if (data.value?.message === "TWO_FACTOR_REQUIRED") {
      router.push("/two-factor")
      userStore.setTwoFactor(data.value.data.two_factor_token)
      userStore.setUser({
        email: values?.email,
      })
      return
    }
    userStore.setToken(data.value.data.access_token)
    router.push("/home")
    toaster.show({
      title: t("success"),
      message: data?.value?.message,
      color: "success",
      icon: "ph:check",
      closable: true,
    })
  } else {
    toaster.show({
      title: t("error"),
      message: data?.value?.message,
      color: "danger",
      icon: "lucide:alert-triangle",
      closable: true,
    })
  }
})
</script>

<style lang="scss">
.login-two {
  .nui-input,
  .nui-select {
    border-width: 1px !important;
    border-color: theme("colors.orange.400") !important;
  }

  .nui-input:hover,
  .nui-select:hover {
    border-color: theme("colors.orange.700") !important;
  }

  .nui-input-icon {
    svg {
      color: theme("colors.orange.500") !important;
    }
  }

  .nui-checkbox {
    .nui-checkbox-inner {
      border-color: theme("colors.orange.500") !important;
    }
    .nui-icon-check {
      color: theme("colors.orange.500") !important;
    }
  }
}
</style>
